import { DateTime } from 'luxon';
import { ColumnStats } from 'smg/admin/hyejoo';
import axios from 'smg/axios';
import Batch from 'smg/batch';
import * as hyejoo from 'smg/customer-api/hyejoo';
import { FromString } from 'smg/html';
import * as models from 'smg/models';
import { OrderPayment } from 'smg/models';
import { Clone } from 'smg/template';
import { DollarFormat } from 'smg/util';
import { PageLoader } from '../../account';
import { Relref } from '../../hugo';

declare interface myWindow extends Window {
    readonly apiRoot: string;
}
declare const window: myWindow;

if (document.body.id === 'account-payment-history') {
    main();
}

export function main() {
    const b = new Batch();

    const allPaymentsCount = b.get<ColumnStats>('/order-payments', {
        params: {
            'colStats': 'id',
        },
    }).then(resp => resp.data.count ?? 0);

    void b.Exec();

    const $tableContainer = document.querySelector('#table-container') as HTMLElement;
    const $cardContainer = document.querySelector('#card-container') as HTMLElement;
    const $noResults = document.querySelector('.no-results') as HTMLElement;
    const $controlsContainer = document.querySelector('.controls-container') as HTMLElement;

    const $table = createPaymentHistoryTable();
    $tableContainer.appendChild($table);
    const $tBody = $table.querySelector('tbody') as HTMLElement;
    let $tableRows: HTMLElement[] = [];

    const $qtyResults = document.querySelector('.controls-container')?.querySelector('.results-qty') as HTMLElement;

    const pl: PageLoader<models.OrderPayment> = new PageLoader<models.OrderPayment>({
        clearResults: true,
        parent: $cardContainer,
        beforeLoad: () => {
            $tableRows = [];
            $tableContainer.classList.add('opacity-50');
        },
        get: () => axios.get<hyejoo.OrderPayments>('/order-payments', {
            params: {
                'sort': 'created desc',
                'max': 5,
                'data': 'total,created,paymentOption,orderPayments,invoiceNumber',
                'p': pl.CurrentPage(),
                'q': pl.Search(),
            },
        }).then(resp => {
            if (resp.data.orderPayments.length === 0) {
                $noResults.classList.remove('d-none');
                $tableContainer.classList.add('d-none');
                $tableContainer.classList.remove('d-sm-block');
                $cardContainer.classList.add('d-none');
                $controlsContainer.classList.add('d-none');
            } else {
                $tableContainer.classList.add('d-sm-block');
                $controlsContainer.classList.remove('d-none');
            }
            return { rows: resp.data.orderPayments, nextPage: resp.data.nextPage };
        }),
        draw: orderPayment => {
            const $card = createPaymentHistoryCard(orderPayment);
            $tableRows.push(createPaymentHistoryTableRow(orderPayment));
            return $card;
        },
        afterLoad: async () => {
            const $currTableRows = Array.from($tBody.querySelectorAll('tr'));
            if ($currTableRows.length && $tableRows.length) {
                for (let i = 0; i < $tableRows.length; i++) {
                    if ($currTableRows[i]) {
                        $currTableRows[i].replaceWith($tableRows[i]);
                    } else {
                        $tBody.appendChild($tableRows[i]);
                    }
                    if (i === $tableRows.length - 1) {
                        const $remainingRows = $currTableRows.slice(i + 1);
                        for (const $remainingRow of $remainingRows) {
                            $remainingRow.remove();
                        }
                    }
                }
            } else {
                for (const $row of $tableRows) {
                    $tBody.appendChild($row);
                }
            }
            $tableContainer.classList.remove('opacity-50');

            const results = Math.min((pl.CurrentPage() + 1) * 5, await allPaymentsCount);
            $qtyResults.textContent = `Showing ${String(results)} of ${await allPaymentsCount} results`;
        },
    });

    pl.Activate();
    pl.Load();
}

function createPaymentHistoryTable(): HTMLElement {
    const $table = FromString(`<table class="table mw-100 overflow-hidden">
                                    <thead>
                                        <tr>
                                            <th style="background-color: #f4f6fc;">Date</th>
                                            <th class="text-end" style="background-color: #f4f6fc;">Order no.</th>
                                            <th class="text-end" style="background-color: #f4f6fc;">Amount</th>
                                            <th class="text-end" style="background-color: #f4f6fc;">Invoice</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>2025-05-26</td>
                                            <td>#12345</td>
                                            <td>$250.00</td>
                                            <td><a href="invoice12345.pdf">Download</a></td>
                                        </tr>
                                    </tbody>
                                </table>`);

    const tbody = $table.querySelector('tbody') as HTMLElement;
    tbody.innerHTML = '';

    return $table;
}

function createPaymentHistoryTableRow(orderPayment: OrderPayment): HTMLElement {
    const orders: models.Order[] = [];
    if (orderPayment.orderPayments && orderPayment.orderPayments.length > 0) {
        for (const oPayment of orderPayment.orderPayments) {
            if (oPayment.order) {
                orders.push(oPayment.order);
            }
        }
    }

    const $orderLinks: HTMLAnchorElement[] = [];
    const $invoiceLinks: HTMLAnchorElement[] = [];
    for (const order of orders) {
        if (order.invoiceNumber) {
            const $ol = FromString(`<a class="order-link text-end" href="${Relref('/account/order')}?id=${order.id || ''}">#${String(order.invoiceNumber)}</a>`);
            $orderLinks.push($ol as HTMLAnchorElement);
        }
        if (order.id) {
            const $il = FromString(`<a class="invoice-link d-flex align-items-center justify-content-center btn btn-link dl-invoice-btn py-0 me-sm-n2" href="${window.apiRoot}/orders/${order.id ?? ''}/invoice">
                                                <span class="material-symbols-rounded pe-md-1">download</span>
                                                <span class="d-none d-md-inline">Download</span>
                                            </a>`);
            $invoiceLinks.push($il as HTMLAnchorElement);
        }
    }

    const $row = FromString(`<tr>
                                    <td style="vertical-align: middle;">${formatDate(orderPayment.created || '') || ''}</td>
                                    <td style="vertical-align: middle;"><div class="order-links-container d-flex flex-column justify-content-center"></div></td>
                                    <td style="vertical-align: middle;" class="text-end">${DollarFormat(orderPayment.total || 0)}</td>
                                    <td style="vertical-align: middle;" class="text-end">
                                        <div class="d-flex flex-column align-items-end justify-content-center invoice-links-container gap-4"></div>
                                    </td>
                                </tr>`);

    const $orderLinksContainer = $row.querySelector('.order-links-container') as HTMLElement;
    const $invoiceLinksContainer = $row.querySelector('.invoice-links-container') as HTMLElement;

    for (const $ol of $orderLinks) {
        $orderLinksContainer.appendChild($ol);
    }
    for (const $il of $invoiceLinks) {
        $invoiceLinksContainer.appendChild($il);
    }

    return $row;
}

function createPaymentHistoryCard(orderPayment: OrderPayment): HTMLElement {
    const $card = Clone('#payment-history-card-template').firstElementChild as HTMLElement;

    const $total = $card.querySelector('.total') as HTMLElement;
    const $orderDate = $card.querySelector('.date') as HTMLElement;

    if (orderPayment.orderPayments && orderPayment.orderPayments.length > 0) {
        if (orderPayment.orderPayments.length === 1) {
            const $orderLinksContainer = $card.querySelector('.order-links-container') as HTMLElement;
            const $invoiceLinksContainer = $card.querySelector('.invoice-links-container') as HTMLElement;

            const $orderLink = Clone('#order-link-template').firstElementChild as HTMLAnchorElement;
            const $invoiceLink = Clone('#invoice-download-template').firstElementChild as HTMLAnchorElement;

            $orderLink.textContent = `Order #${String(orderPayment.orderPayments[0].order?.invoiceNumber)}`;
            $orderLink.href = `${Relref('/account/order')}?id=${orderPayment.orderPayments[0].order?.id || ''}`;
            $invoiceLink.href = `${window.apiRoot}/orders/${orderPayment.orderPayments[0].order?.id ?? ''}/invoice`;

            $orderLinksContainer.appendChild($orderLink);
            $invoiceLinksContainer.appendChild($invoiceLink);
            $total.textContent = DollarFormat(orderPayment.total || 0);
        } else {
            const $multipleOrdersPaymentContainer = $card.querySelector('.multiple-orders-payment-container') as HTMLElement;
            $multipleOrdersPaymentContainer.classList.remove('d-none');
            for (const oPayment of orderPayment.orderPayments) {
                if (oPayment.order) {
                    const $multipleOrdersLink = Clone('#multiple-orders-payment-template').firstElementChild as HTMLElement;
                    const $total = $multipleOrdersLink.querySelector('.total') as HTMLElement;
                    const $orderLink = $multipleOrdersLink.querySelector('.order') as HTMLAnchorElement;
                    const $invoiceLink = $multipleOrdersLink.querySelector('.invoice') as HTMLAnchorElement;

                    $total.textContent = DollarFormat(oPayment.total || 0);
                    $orderLink.textContent = `Order #${String(oPayment.order.invoiceNumber)}`;
                    $orderLink.href = `${Relref('/account/order')}?id=${oPayment.order.id || ''}`;
                    $invoiceLink.href = `${window.apiRoot}/orders/${oPayment.order.id ?? ''}/invoice`;

                    $multipleOrdersPaymentContainer.appendChild($multipleOrdersLink);
                }
            }
        }
    }

    $orderDate.textContent = formatDate(orderPayment.created || '') || '';

    return $card;
}

function formatDate(isoString: string): string {
    const date = new Date(isoString);
    return DateTime.fromJSDate(date).toLocaleString(DateTime.DATETIME_MED);
}